import React, { useEffect, useState, useRef, useCallback } from "react";
import { gselectedgroup, gloggedinuser } from "../../interfaces";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import styles from "./TwiLMWebCCA.module.css";
import { genNewConvoKey } from "./../../util_glob";
declare global {
    interface Window {
        Twilio: any;
    }
}

interface TwilioCallProps {
    selectedGroup: gselectedgroup;
    loggedInUser: gloggedinuser; // New prop for identifying the user
    activeConvoKey: string;
    histGetByKey: (userid: string, key: string) => void;
    newConversation: (convoKey?: string) => void; // New prop for creating a new conversation
}

const TwiLMWebCCA: React.FC<TwilioCallProps> = ({ selectedGroup, loggedInUser, activeConvoKey, histGetByKey, newConversation }) => {
    const [status, setStatus] = useState("Click 'Call' to connect.");
    const [callDisabled, setCallDisabled] = useState(false);
    const [endDisabled, setEndDisabled] = useState(true);
    const [isInitializing, setIsInitializing] = useState(false);

    const tokenExpiry = useRef<number | null>(null);
    const twilioReady = useRef(false);
    const tokenIntervalIdRef = useRef<NodeJS.Timeout | null>(null);
    const isInitializingRef = useRef(isInitializing);
    useEffect(() => {
        isInitializingRef.current = isInitializing;
    }, [isInitializing]);

    useEffect(() => {
        let intervalId: any = null; // To store the interval ID
        console.log("TwiLMWebCCA:", activeConvoKey);
        // Check if the condition to run the interval is met
        if (endDisabled === false) {
            // Start the interval
            intervalId = setInterval(() => {
                console.log("Call is active (endDisabled is false). Calling histGetByKey...");
                histGetByKey(loggedInUser.userId, activeConvoKey);
            }, 2000); // Calls every 2 seconds
            console.log("Interval started with ID:", intervalId);
        } else {
            console.log("Call is not active (endDisabled is true). Interval not started or will be cleared.");
        }
        return () => {
            if (intervalId) {
                console.log("Cleaning up interval with ID:", intervalId);
                clearInterval(intervalId);
            }
        };
    }, [endDisabled, loggedInUser, activeConvoKey]); // Dependencies for the effect

    const twi_acc = selectedGroup.line_config.account_sid;
    let twi_lmapp = selectedGroup.line_config.twi_lmapp;
    import.meta.env.DEV === true && (twi_lmapp = "AP90d19dad5bccbe90447a6b1758d48b68");

    const clearTokenRefreshInterval = useCallback(() => {
        if (tokenIntervalIdRef.current) {
            clearInterval(tokenIntervalIdRef.current);
            tokenIntervalIdRef.current = null;
            console.log("Token refresh interval cleared.");
        }
    }, []);

    const fetchToken = useCallback(async (): Promise<string | null> => {
        console.log("Fetching Twilio token...");
        try {
            const res = await fetch(`/kgnf_twi_to?twi_acc=${twi_acc}&twi_lmapp=${twi_lmapp}`);
            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`Failed to fetch token: ${res.status} ${res.statusText}. Details: ${errorText}`);
            }
            const data = await res.json();
            if (!data.token) throw new Error("Token not found in response");

            const payload = JSON.parse(atob(data.token.split(".")[1]));
            const exp = payload.exp * 1000;
            tokenExpiry.current = exp;
            const now = Date.now();
            if (exp - now <= 0) {
                console.warn("Fetched token is already expired.");
                return null;
            }
            return data.token;
        } catch (err: any) {
            console.error("Fetch token error:", err);
            setStatus(`Error fetching token: ${err.message}`);
            tokenExpiry.current = null;
            return null;
        }
    }, [twi_acc, twi_lmapp]);

    const startTokenRefreshInterval = useCallback(() => {
        clearTokenRefreshInterval();

        const TOKEN_REFRESH_INTERVAL_MS = 60000;
        const TOKEN_EXPIRY_THRESHOLD_MS = 2 * 60000;

        console.log("Starting token refresh interval.");
        tokenIntervalIdRef.current = setInterval(async () => {
            if (!twilioReady.current) return;
            const now = Date.now();
            if (tokenExpiry.current && tokenExpiry.current - now < TOKEN_EXPIRY_THRESHOLD_MS) {
                console.log("Token expiring soon or has expired. Refreshing token.");
                setStatus("Refreshing Twilio token...");
                const newToken = await fetchToken();
                if (newToken && window.Twilio && window.Twilio.Device) {
                    try {
                        window.Twilio.Device.setup(newToken);
                        console.log("Token refresh attempt submitted to Twilio.Device.setup().");
                    } catch (err: any) {
                        setStatus(`Error updating token: ${err.message}`);
                        console.error("Error re-setting up Twilio with new token:", err);
                        twilioReady.current = false;
                        setCallDisabled(true);
                    }
                } else if (!newToken) {
                    setStatus("Failed to refresh token. Calls may fail.");
                    twilioReady.current = false;
                    setCallDisabled(true);
                }
            }
        }, TOKEN_REFRESH_INTERVAL_MS);
    }, [clearTokenRefreshInterval, fetchToken]);

    const initTwilioDevice = useCallback((): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (!window.Twilio || !window.Twilio.Device) {
                const errMsg = "Twilio SDK not available for device initialization.";
                console.error(errMsg);
                reject(new Error(errMsg));
                return;
            }
            const token = await fetchToken();
            if (!token) {
                const errMsg = "Failed to initialize: Could not get a valid token.";
                setStatus(errMsg);
                reject(new Error(errMsg));
                return;
            }
            let setupFinalized = false;
            const onReady = () => {
                if (setupFinalized) return;
                setupFinalized = true;
                setStatus("Device Ready. You can make a call.");
                setCallDisabled(false);
                setEndDisabled(true);
                twilioReady.current = true;
                console.log("Twilio Device Ready (Initial Setup)");
                startTokenRefreshInterval();
                resolve();
            };
            const onError = (error: any) => {
                console.error("Twilio Device Error:", error.code, error.message, error);
                if (isInitializingRef.current && !setupFinalized) {
                    setupFinalized = true;
                    setStatus(`Twilio Setup Error: ${error.message}`);
                    twilioReady.current = false;
                    reject(error);
                } else if (twilioReady.current) {
                    setStatus(`Error: ${error.message}`);
                    twilioReady.current = false;
                    setCallDisabled(true);
                    setEndDisabled(true);
                }
            };
            window.Twilio.Device.removeAllListeners("ready");
            window.Twilio.Device.on("ready", onReady);
            window.Twilio.Device.removeAllListeners("error");
            window.Twilio.Device.on("error", onError);
            window.Twilio.Device.removeAllListeners("connect");
            window.Twilio.Device.on("connect", (connection: any) => {
                setStatus("Call Connected.");
                setCallDisabled(true);
                setEndDisabled(false);
                console.log("Twilio Call Connected", connection);
            });
            window.Twilio.Device.removeAllListeners("disconnect");
            window.Twilio.Device.on("disconnect", () => {
                setStatus("Call Disconnected.");
                setCallDisabled(false);
                setEndDisabled(true);
                if (window.Twilio?.Device?.status() !== "offline") {
                    twilioReady.current = true;
                }
                console.log("Twilio Call Disconnected");
            });
            window.Twilio.Device.removeAllListeners("incoming");
            window.Twilio.Device.on("incoming", (connection: any) => {
                setStatus(`Incoming call from ${connection.parameters.From}`);
                console.log("Twilio Incoming Call", connection);
            });
            try {
                console.log("Setting up Twilio Device with token...");
                window.Twilio.Device.setup(token, {
                    /* debug: true */
                });
            } catch (err: any) {
                if (!setupFinalized) {
                    setupFinalized = true;
                    setStatus(`Twilio Device.setup exception: ${err.message}`);
                    console.error("Twilio.Device.setup direct exception:", err);
                    twilioReady.current = false;
                    reject(err);
                }
            }
        });
    }, [fetchToken, startTokenRefreshInterval]);

    const loadTwilioScriptAndInit = useCallback(async (): Promise<void> => {
        if (window.Twilio) {
            console.log("Twilio SDK already loaded. Initializing device...");
            return initTwilioDevice();
        }
        console.log("Loading Twilio SDK...");
        return new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://sdk.twilio.com/js/client/v1.13/twilio.js";
            script.async = true;
            script.onload = async () => {
                console.log("Twilio SDK loaded successfully. Initializing device...");
                try {
                    await initTwilioDevice();
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };
            script.onerror = err => {
                const errMsg = "Failed to load Twilio SDK.";
                setStatus(errMsg);
                console.error(errMsg, err);
                reject(new Error(errMsg));
            };
            document.body.appendChild(script);
        });
    }, [initTwilioDevice]);

    const handleCall = async () => {
        if (isInitializing) {
            console.log("Initialization already in progress.");
            return;
        }

        // zza prep params for twilio
        let convoKey = genNewConvoKey(selectedGroup.assistanttype);
        const callParams = {
            ClientIdentifier: convoKey,
            SpaceIdentifier: selectedGroup.selectionId
        };

        if (!twilioReady.current) {
            setIsInitializing(true);
            setCallDisabled(true);
            setStatus("Initializing Twilio & Connecting...");
            console.log("Twilio not ready. Starting initialization...");
            try {
                await loadTwilioScriptAndInit();
                if (twilioReady.current) {
                    console.log(`Initialization successful. Attempting to connect call for user: ${activeConvoKey}.`);

                    newConversation(convoKey);

                    setStatus("Connecting call...");
                    setCallDisabled(true);
                    // Create a new conversation
                    window.Twilio.Device.connect(callParams); // Pass params
                } else {
                    setStatus("Initialization completed, but device not ready. Please try again.");
                    setCallDisabled(false);
                }
            } catch (error: any) {
                setStatus(`Initialization Failed: ${error.message}`);
                console.error("handleCall: Initialization or connection failed:", error);
                setCallDisabled(false);
                twilioReady.current = false;
            } finally {
                setIsInitializing(false);
            }
        } else {
            console.log(`Twilio ready. Attempting to connect call for user: ${loggedInUser.userId}.`);
            newConversation(convoKey);
            setStatus("Connecting call...");
            setCallDisabled(true);
            window.Twilio.Device.connect(callParams); // Pass params
        }
    };

    const handleEnd = () => {
        if (window.Twilio && window.Twilio.Device) {
            console.log("Ending call via disconnectAll().");
            setStatus("Ending call...");
            window.Twilio.Device.disconnectAll();
        }
    };

    useEffect(() => {
        return () => {
            console.log("TwiLMWebCCA: Unmounting. Cleaning up Twilio Device and intervals.");
            if (window.Twilio && window.Twilio.Device) {
                if (typeof window.Twilio.Device.destroy === "function") {
                    window.Twilio.Device.destroy();
                    console.log("Twilio Device destroyed.");
                }
            }
            clearTokenRefreshInterval();
            twilioReady.current = false;
        };
    }, [clearTokenRefreshInterval]);

    return (
        <div className={styles.container}>
            <div className={styles.status}>{status}</div>
            <PrimaryButton
                text="📞 Call"
                onClick={handleCall}
                disabled={callDisabled || isInitializing}
                styles={{
                    root: {
                        width: "100%",
                        height: 30,
                        fontSize: 16,
                        fontWeight: 600,
                        marginTop: "5px",
                        backgroundColor: "#38a169",
                        color: "white",
                        border: "none",
                        borderRadius: 4,
                        marginBottom: 5,
                        cursor: callDisabled || isInitializing ? "not-allowed" : "pointer",
                        opacity: callDisabled || isInitializing ? 0.6 : 1
                    },
                    rootHovered: { backgroundColor: "#2f855a", color: "white", border: "none" },
                    rootPressed: { backgroundColor: "#276749", color: "white", border: "none" },
                    label: { overflow: "visible", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                }}
            />
            <DefaultButton
                text="❌ End"
                onClick={handleEnd}
                disabled={endDisabled}
                styles={{
                    root: {
                        width: "100%",

                        height: 30,
                        fontSize: 16,
                        fontWeight: 600,
                        marginRight: 30,
                        backgroundColor: "#e53e3e",
                        color: "white",

                        marginTop: "5px",
                        borderRadius: 4,
                        cursor: endDisabled ? "not-allowed" : "pointer",
                        opacity: endDisabled ? 0.6 : 1
                    },
                    rootHovered: { backgroundColor: "#c53030", color: "white", border: "none" },
                    rootPressed: { backgroundColor: "#a02727", color: "white", border: "none" },
                    label: { overflowY: "visible", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                }}
            />
        </div>
    );
};

export default TwiLMWebCCA;
